'use client';

import type { ReactNode } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { ArrowRight, ClipboardList } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

import { INTERACTION_PAYWALL_ID } from '../lib/paywall';

// Must match the cheapest tier price in subscription-tiers.config.ts
const PRICE_FROM_PLN = 79;

function buildUrlWithNext(base: string, returnPath: string | null) {
  if (!returnPath || returnPath === '/' || returnPath.startsWith('/auth/'))
    return base;
  return `${base}?next=${encodeURIComponent(returnPath)}`;
}

export type PaywallVariant = 'anonymous' | 'expired' | 'trial-available';

export interface InteractionPaywallProps {
  variant?: 'anonymous' | 'expired';
  /** When false, the expired variant swaps price + /pricing CTA for a contact CTA */
  showPricing: boolean;
  interactionCount?: number;
  /** Number of critical + important severity interactions */
  severeCount?: number;
  children: ReactNode;
  contentVisible?: boolean;
}

function InteractionCountHero({ count }: { count: number }) {
  return (
    <p className="mt-0 mb-2 flex items-center justify-center gap-2 text-xl font-semibold text-zinc-700 sm:gap-3 sm:text-3xl dark:text-zinc-200">
      <ClipboardList
        className="h-6 w-6 shrink-0 text-zinc-600 sm:h-9 sm:w-9 dark:text-zinc-400"
        strokeWidth={2.3}
        style={{
          filter:
            'drop-shadow(0 2px 8px rgba(245, 195, 68, 0.7)) drop-shadow(0 4px 20px rgba(245, 195, 68, 0.5))',
          animation: 'paywall-icon-pulse 2.5s ease-in-out infinite',
        }}
      />
      <span>
        <Trans
          i18nKey="interactions:paywall.found"
          count={count}
          components={{
            b: <span className="font-black text-zinc-900 dark:text-white" />,
          }}
        />
      </span>
    </p>
  );
}

type VariantKey = 'anonymous' | 'expired' | 'expiredNoPricing';

const VARIANT_URLS: Record<
  VariantKey,
  {
    hasFooter: boolean;
    getCtaUrl: (pathname: string | null) => string;
    getFooterUrl: (pathname: string | null) => string;
  }
> = {
  anonymous: {
    hasFooter: true,
    getCtaUrl: (pathname) => buildUrlWithNext('/auth/sign-up', pathname),
    getFooterUrl: (pathname) => buildUrlWithNext('/auth/sign-in', pathname),
  },
  expired: {
    hasFooter: true,
    getCtaUrl: () => '/pricing',
    getFooterUrl: () => '/contact',
  },
  expiredNoPricing: {
    hasFooter: false,
    getCtaUrl: () => '/contact',
    getFooterUrl: () => '/contact',
  },
};

export function InteractionPaywall({
  variant,
  showPricing,
  interactionCount,
  severeCount = 0,
  children,
  contentVisible = false,
}: InteractionPaywallProps) {
  const { t } = useTranslation('interactions');
  const pathname = usePathname();
  const hasInteractions = interactionCount != null && interactionCount > 0;

  if (contentVisible) {
    return (
      <div
        id={INTERACTION_PAYWALL_ID}
        data-paywall="interaction-results"
        className="paywall"
      >
        {children}
      </div>
    );
  }

  const variantKey: VariantKey =
    variant === 'expired' && !showPricing
      ? 'expiredNoPricing'
      : (variant ?? 'anonymous');
  const urls = VARIANT_URLS[variantKey];

  return (
    <div className="flex flex-col">
      <div
        id={INTERACTION_PAYWALL_ID}
        data-paywall="interaction-results"
        className="paywall relative right-1/2 left-1/2 -mr-[50vw] -ml-[50vw] max-h-[120px] w-screen overflow-hidden sm:max-h-[200px]"
      >
        <div className="pointer-events-none mx-auto max-w-3xl px-8 pt-4 select-none sm:px-0">
          <div className="blur-[5px]" aria-hidden="true">
            {children}
          </div>
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[80px] bg-gradient-to-t from-[#efece4] via-[#efece4]/80 to-transparent sm:h-[160px] dark:from-zinc-900 dark:via-zinc-900/80" />
      </div>

      <div
        data-nosnippet
        className="relative right-1/2 left-1/2 -mr-[50vw] -ml-[50vw] w-screen px-6 pb-16 sm:pb-56 dark:bg-zinc-900"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgb(0 0 0 / 0.06) 1px, transparent 1px), linear-gradient(to bottom, #efece4 0%, transparent 100%)',
          backgroundSize: '20px 20px, 100% 100%',
        }}
      >
        <div className="mx-auto max-w-2xl text-center">
          {hasInteractions ? (
            <InteractionCountHero count={interactionCount!} />
          ) : (
            <div className="mb-6" />
          )}

          <p
            className={
              variant !== 'expired' && severeCount > 0
                ? 'mb-5 text-[11px] font-semibold tracking-wider text-zinc-400 sm:mb-8 sm:text-sm sm:tracking-widest dark:text-zinc-500'
                : 'mb-5 text-[10px] font-semibold tracking-wider text-zinc-400 uppercase sm:mb-8 sm:text-xs sm:tracking-widest dark:text-zinc-500'
            }
          >
            {variant === 'expired' ? (
              t(`paywall.${variantKey}.label`)
            ) : severeCount > 0 ? (
              <Trans
                i18nKey="interactions:paywall.severe"
                count={severeCount}
                components={{ b: <span className="font-black" /> }}
              />
            ) : (
              t('paywall.seeDetails')
            )}
          </p>

          <Button
            size="2xl"
            asChild
            className="flex-col gap-0.5 max-sm:px-4 max-sm:py-2.5 max-sm:text-sm"
          >
            <Link href={urls.getCtaUrl(pathname)}>
              <span className="flex items-center gap-2">
                {t(`paywall.${variantKey}.ctaText`)}
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </span>
              <span className="text-[10px] font-normal text-zinc-300 sm:text-xs dark:text-zinc-500">
                {t(`paywall.${variantKey}.ctaSubtext`, {
                  price: PRICE_FROM_PLN,
                })}
              </span>
            </Link>
          </Button>
          {urls.hasFooter && (
            <p className="mt-4 text-xs text-zinc-500 sm:mt-6 sm:text-sm dark:text-zinc-400">
              {t(`paywall.${variantKey}.footerText`)}{' '}
              <Link
                href={urls.getFooterUrl(pathname)}
                className="font-medium text-zinc-900 underline underline-offset-4 dark:text-white"
              >
                {t(`paywall.${variantKey}.footerLinkText`)}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
